import { Fragment } from 'react';
import './InfoPage.css';
import { useI18n } from './i18n';
import Icon from './Icon';

export const INFO_LINKS = [
  { key: 'about' },
  { key: 'pricing' },
  { key: 'help' },
  { key: 'terms' },
  { key: 'privacy' },
  { key: 'contact' },
];

// Long-form content for the info pages, per language. Each page is a list of
// blocks: ['lead'|'meta'|'h'|'p'|'note', text] or ['ul', [items]].
const PAGES = {
  about: {
    en: {
      title: 'About iBetterAgent',
      blocks: [
        ['lead', `iBetterAgent is the relationship engine for real estate agents — it keeps you top-of-mind with every past client automatically, so the repeat business and referrals you've already earned actually come back to you.`],
        ['h', `The problem we solve`],
        ['p', `Most of an agent's future income comes from past clients and referrals — yet within months of closing, most of those relationships go quiet. Not because you don't care, but because you're busy. iBetterAgent makes consistent, personal follow-up effortless, so you're always the agent your clients remember when it's time to move again.`],
        ['h', `What you get`],
        ['ul', [
          `Automated, personal touches — birthdays, home-sale anniversaries, and holidays go out on schedule, in your voice. You never press send.`,
          `A professional public profile — a shareable page with your track record, sold listings, and client reviews that builds trust before you even pick up the phone.`,
          `Know who's paying attention — every click on your profile link is scored, so you can see which clients are warming up right now and reach out at the perfect moment.`,
          `Effortless reviews — request and showcase client testimonials in a couple of clicks.`,
          `Everything in one place — import your contacts and closings once; reusable, multi-language templates keep every message personal.`,
        ]],
        ['h', `Why it works`],
        ['p', `Staying in touch is the highest-ROI activity in real estate — and the easiest to let slip. By making it automatic, iBetterAgent turns the clients you've already earned into a steady, compounding stream of repeat business and referrals, without adding one more thing to your day.`],
        ['h', `Who it's for`],
        ['p', `Solo agents and teams who want to build a business on lasting relationships — turning one-time buyers and sellers into lifelong clients — without spreadsheets, sticky notes, or remembering every date themselves.`],
      ],
    },
    zh: {
      title: '关于 iBetterAgent',
      blocks: [
        ['lead', `iBetterAgent 是房地产经纪人的客户关系引擎——它自动帮您与每一位老客户保持联系，让您辛苦赢得的重复成交与转介绍真正回到您身边。`],
        ['h', `我们要解决的问题`],
        ['p', `经纪人未来的收入，绝大部分来自老客户与转介绍——然而成交后仅数月，大多数客户关系便悄然沉寂。这并非因为您不用心，而是因为您太忙了。iBetterAgent 让持续而个性化的跟进变得轻松，使您在客户下次需要买卖房产时，始终是他们第一个想起的人。`],
        ['h', `您将获得什么`],
        ['ul', [
          `自动、贴心的问候——生日、购房周年和节日祝福按计划、以您的口吻自动送出，您无需亲自点击发送。`,
          `专业的公开主页——一个可分享的页面，展示您的成交业绩、售出房源和客户好评，在您开口之前就已建立信任。`,
          `看得见谁在关注——每一次点击您主页链接的行为都会被评分，让您清楚哪些客户正在"升温"，从而在最佳时机主动联系。`,
          `轻松收集好评——只需几步即可邀请并展示客户的真实评价。`,
          `一站式管理——只需一次导入联系人与成交记录；可重复使用的多语言模板让每条消息都保持个性化。`,
        ]],
        ['h', `为什么有效`],
        ['p', `保持联系，是房地产行业中回报率最高、却也最容易被忽略的一件事。iBetterAgent 让它自动完成，把您已经赢得的客户，变成源源不断、日益累积的重复成交与转介绍——而无需在您的日程上再多添一项。`],
        ['h', `适合谁使用`],
        ['p', `适合希望以长久关系建立事业的独立经纪人和团队——把一次性买家和卖家变成终身客户——无需表格、便利贴，也不必自己记住每一个日期。`],
      ],
    },
    es: {
      title: 'Acerca de iBetterAgent',
      blocks: [
        ['lead', `iBetterAgent es el motor de relaciones para agentes inmobiliarios: te mantiene presente ante cada cliente anterior de forma automática, para que el negocio recurrente y las referencias que ya te ganaste realmente regresen a ti.`],
        ['h', `El problema que resolvemos`],
        ['p', `La mayor parte de los ingresos futuros de un agente provienen de clientes anteriores y referencias, pero a los pocos meses del cierre la mayoría de esas relaciones se apagan. No porque no te importe, sino porque estás ocupado. iBetterAgent hace que el seguimiento constante y personal sea fácil, para que siempre seas el agente que tus clientes recuerdan cuando llega el momento de mudarse de nuevo.`],
        ['h', `Qué obtienes`],
        ['ul', [
          `Gestos automáticos y personales: cumpleaños, aniversarios de venta y saludos de temporada salen a tiempo, con tu voz. Nunca presionas enviar.`,
          `Un perfil público profesional: una página para compartir con tu trayectoria, propiedades vendidas y reseñas de clientes que genera confianza antes de que levantes el teléfono.`,
          `Descubre quién presta atención: cada clic en el enlace de tu perfil se puntúa, para que veas qué clientes se están interesando ahora y los contactes en el momento perfecto.`,
          `Reseñas sin esfuerzo: solicita y muestra testimonios de clientes en un par de clics.`,
          `Todo en un solo lugar: importa tus contactos y cierres una vez; las plantillas reutilizables y multilingües mantienen cada mensaje personal.`,
        ]],
        ['h', `Por qué funciona`],
        ['p', `Mantenerse en contacto es la actividad de mayor retorno en el sector inmobiliario, y la más fácil de descuidar. Al hacerlo automático, iBetterAgent convierte a los clientes que ya ganaste en un flujo constante y creciente de negocio recurrente y referencias, sin añadir una sola tarea más a tu día.`],
        ['h', `Para quién es`],
        ['p', `Para agentes individuales y equipos que quieren construir un negocio basado en relaciones duraderas —convirtiendo a compradores y vendedores ocasionales en clientes de por vida— sin hojas de cálculo, notas adhesivas ni tener que recordar cada fecha.`],
      ],
    },
  },

  pricing: {
    en: {
      title: 'Simple, Honest Pricing',
      blocks: [
        ['offer', [`⏳ Limited-time launch offer`, `90 days FREE — triple the normal trial`, `Every feature unlocked · No credit card needed`]],
        ['lead', `iBetterAgent normally comes with a 30-day free trial. Right now, new agents get a full 90 days free — no credit card, no auto-charge, nothing to cancel.`],
        ['h', `Your free trial`],
        ['p', `Every new account starts with a free trial of the complete product: automated reminders, your public profile with reviews, contact engagement scores, imports, multilingual messages — everything. The standard trial is 30 days, but for a limited time we're tripling it: sign up today and yours runs a full 90 days.`],
        ['h', `Then $10 per month`],
        ['p', `After your trial, keep everything for $10/month. One referred client pays for decades of iBetterAgent.`],
        ['ul', [
          `All features included — there are no tiers, add-ons, or upsells.`,
          `Unlimited contacts, properties, and reminder emails.`,
          `Your public profile stays live, with your reviews and track record.`,
          `Cancel anytime — your data is always yours to export.`,
        ]],
        ['h', `Brokerages, teams & resellers`],
        ['p', `Running an office or a group? Corporate and reseller/group pricing is available — seat-based licensing, office-wide templates with your brand, and zero-training onboarding for your agents. Email support@ibetteragent.com and we'll put together the right plan.`],
        ['cta', [`Claim your 90-day free trial →`, `/?signup=1`]],
        ['note', `Questions before you start? Email support@ibetteragent.com — we read every message.`],
      ],
    },
    zh: {
      title: '简单透明的价格',
      blocks: [
        ['offer', [`⏳ 限时上线特惠`, `免费 90 天——三倍于标准试用期`, `全部功能开放 · 无需信用卡`]],
        ['lead', `iBetterAgent 标准免费试用期为 30 天。现在注册，新经纪人可享整整 90 天免费——无需信用卡，不会自动扣费，无需取消。`],
        ['h', `您的免费试用`],
        ['p', `每个新账户都从完整产品的免费试用开始：自动提醒、带客户好评的公开主页、客户互动评分、数据导入、多语言消息——全部包含。标准试用期为 30 天，限时特惠期间翻三倍：今天注册，即享整整 90 天。`],
        ['h', `之后每月 $10`],
        ['p', `试用期结束后，仅需每月 $10 即可继续使用全部功能。一位转介绍客户，就足以支付几十年的 iBetterAgent 费用。`],
        ['ul', [
          `包含所有功能——没有分级、附加项或升级推销。`,
          `联系人、房产和提醒邮件数量不限。`,
          `您的公开主页持续在线，展示您的好评与业绩。`,
          `随时取消——您的数据永远属于您，可随时导出。`,
        ]],
        ['h', `经纪公司、团队与渠道合作`],
        ['p', `经营办公室或团队？我们提供企业版及渠道/团体价格——按席位授权、带您品牌的全办公室模板、经纪人零培训上手。请发邮件至 support@ibetteragent.com，我们将为您定制合适的方案。`],
        ['cta', [`领取 90 天免费试用 →`, `/?signup=1`]],
        ['note', `开始前有疑问？发邮件至 support@ibetteragent.com——每封邮件我们都会认真阅读。`],
      ],
    },
    es: {
      title: 'Precios simples y honestos',
      blocks: [
        ['offer', [`⏳ Oferta de lanzamiento por tiempo limitado`, `90 días GRATIS — el triple de la prueba normal`, `Todas las funciones · Sin tarjeta de crédito`]],
        ['lead', `iBetterAgent normalmente incluye una prueba gratis de 30 días. Por tiempo limitado, los agentes nuevos reciben 90 días completos gratis — sin tarjeta de crédito, sin cargos automáticos, nada que cancelar.`],
        ['h', `Tu prueba gratis`],
        ['p', `Cada cuenta nueva comienza con una prueba gratis del producto completo: recordatorios automáticos, tu perfil público con reseñas, puntuaciones de interés de contactos, importaciones, mensajes multilingües — todo. La prueba estándar es de 30 días, pero por tiempo limitado la triplicamos: regístrate hoy y la tuya dura 90 días completos.`],
        ['h', `Después, $10 al mes`],
        ['p', `Al terminar tu prueba, conserva todo por $10/mes. Un solo cliente referido paga décadas de iBetterAgent.`],
        ['ul', [
          `Todas las funciones incluidas — no hay niveles, extras ni ventas adicionales.`,
          `Contactos, propiedades y correos de recordatorio ilimitados.`,
          `Tu perfil público sigue en línea, con tus reseñas y trayectoria.`,
          `Cancela cuando quieras — tus datos siempre son tuyos y puedes exportarlos.`,
        ]],
        ['h', `Corredurías, equipos y revendedores`],
        ['p', `¿Diriges una oficina o un grupo? Hay precios corporativos y para revendedores/grupos: licencias por puesto, plantillas para toda la oficina con tu marca y incorporación sin capacitación para tus agentes. Escribe a support@ibetteragent.com y armaremos el plan adecuado.`],
        ['cta', [`Reclama tus 90 días gratis →`, `/?signup=1`]],
        ['note', `¿Preguntas antes de empezar? Escribe a support@ibetteragent.com — leemos cada mensaje.`],
      ],
    },
  },

  help: {
    en: {
      title: 'Help & Getting Started',
      blocks: [
        ['lead', `Get set up in about 20 minutes, then iBetterAgent keeps you in front of your clients automatically. After you sign in, the Get Started checklist walks you through the steps below.`],
        ['link', [`📘 Prefer the full walkthrough? Open the Agent Quick Start Guide — every step in detail, with a table of contents. Print it or keep the link.`, `/agent-quick-start.html`]],
        ['h', `1. Publish your public profile`],
        ['p', `In Profile → Public Profile, add a photo, a short introduction, and your contact details, then turn on Publish. You get a shareable page at ibetteragent.com/agent/your-name that clients can view without logging in.`],
        ['h', `2. Set your email signature`],
        ['p', `Open Profile → Email Signature and add your name, title, brokerage, and phone. Use “Insert my profile link” so clients can click through to your profile — every click is tracked and surfaces your most engaged contacts.`],
        ['h', `3. Import your contacts`],
        ['p', `On the Contacts page, click Import Contacts and upload your list. Not sure how to get the file? Click the ⓘ next to the button for step-by-step export instructions. Re-importing the same file never creates duplicates, and new people start as Leads.`],
        ['h', `4. Import your past sales`],
        ['p', `On the Properties page, import your Buyer's Agent Sales and Listing Agent Sales — each has an ⓘ with the export steps. Listing-side sales link automatically to your contacts, and the sold date powers the “happy home anniversary” reminder. Your sales also build the track record shown on your public profile.`],
        ['h', `5. Let the reminders run`],
        ['p', `Reminders — birthdays, home-sale anniversaries, and holidays — send automatically on schedule. You don't press send. Review Assign Reminders to see what's set up, and add details like birthdays where you have them.`],
        ['h', `Common questions`],
        ['ul', [
          `Signing in: your email is your username. Forgot your password? Use “Forgot password?” on the sign-in screen.`,
          `A birthday reminder isn't sending: the contact needs a birthday on file — add one and it activates automatically.`,
          `Import buttons are missing: your MLS must be set (it's chosen when you sign up) to enable importing.`,
          `Clicks on your profile link aren't being credited: make sure it's inserted as a real link in your signature via “Insert my profile link”.`,
        ]],
        ['note', `Need a hand? Contact support at support@iBetterAgent.com.`],
      ],
    },
    zh: {
      title: '帮助与入门',
      blocks: [
        ['lead', `大约 20 分钟即可完成设置，之后 iBetterAgent 会自动帮您与客户保持联系。登录后，“开始使用”清单会引导您完成以下步骤。`],
        ['link', [`📘 想看完整教程？打开《Agent Quick Start Guide》（英文）——包含每一步的详细说明和目录，可打印或收藏链接。`, `/agent-quick-start.html`]],
        ['h', `1. 发布您的公开主页`],
        ['p', `在“个人资料 → 公开主页”中，添加照片、简短介绍和联系方式，然后开启“发布”。您将获得一个可分享的主页 ibetteragent.com/agent/您的名字，客户无需登录即可查看。`],
        ['h', `2. 设置您的邮件签名`],
        ['p', `打开“个人资料 → 邮件签名”，填写您的姓名、职称、经纪公司和电话。使用“插入我的主页链接”，让客户可以点击进入您的主页——每次点击都会被记录，并突出显示您最活跃的客户。`],
        ['h', `3. 导入您的联系人`],
        ['p', `在“联系人”页面，点击“导入联系人”并上传您的名单。不确定如何获取文件？点击按钮旁的 ⓘ 查看逐步导出说明。重复导入同一文件不会产生重复记录，新联系人会以“潜在客户”身份加入。`],
        ['h', `4. 导入您的成交记录`],
        ['p', `在“房产”页面，导入您的“买方经纪成交”和“卖方经纪成交”——每个都有 ⓘ 提供导出步骤。卖方经纪成交会自动关联到您的联系人，成交日期将驱动“乔迁周年”提醒。您的成交记录还会构成公开主页上展示的业绩。`],
        ['h', `5. 让提醒自动运行`],
        ['p', `提醒——生日、乔迁周年和节日——都会按排程自动发送，您无需手动发送。可在“分配提醒”中查看已设置的内容，并为掌握生日等信息的联系人补充相关资料。`],
        ['h', `常见问题`],
        ['ul', [
          `登录：您的邮箱即用户名。忘记密码？在登录界面使用“忘记密码？”。`,
          `生日提醒没有发送：该联系人需要先填写生日——添加后会自动激活。`,
          `找不到导入按钮：需先设置您的 MLS（在注册时选择）才能启用导入。`,
          `主页链接的点击未被记录：请确保通过“插入我的主页链接”将其作为真实链接插入签名中。`,
        ]],
        ['note', `需要帮助？请联系支持邮箱 support@iBetterAgent.com。`],
      ],
    },
    es: {
      title: 'Ayuda e introducción',
      blocks: [
        ['lead', `Configúralo en unos 20 minutos y iBetterAgent mantendrá el contacto con tus clientes automáticamente. Al iniciar sesión, la lista “Empezar” te guía por los pasos siguientes.`],
        ['link', [`📘 ¿Prefieres la guía completa? Abre la Agent Quick Start Guide (en inglés): cada paso en detalle, con índice. Imprímela o guarda el enlace.`, `/agent-quick-start.html`]],
        ['h', `1. Publica tu perfil público`],
        ['p', `En Perfil → Perfil público, agrega una foto, una breve introducción y tus datos de contacto, y activa Publicar. Obtendrás una página para compartir en ibetteragent.com/agent/tu-nombre que los clientes pueden ver sin iniciar sesión.`],
        ['h', `2. Configura tu firma de correo`],
        ['p', `Abre Perfil → Firma de correo y agrega tu nombre, título, agencia y teléfono. Usa “Insertar el enlace a mi perfil” para que los clientes puedan visitar tu perfil: cada clic se registra y resalta a tus contactos más interesados.`],
        ['h', `3. Importa tus contactos`],
        ['p', `En la página de Contactos, haz clic en Importar contactos y sube tu lista. ¿No sabes cómo obtener el archivo? Haz clic en el ⓘ junto al botón para ver las instrucciones de exportación paso a paso. Reimportar el mismo archivo nunca crea duplicados, y los nuevos contactos entran como Prospectos.`],
        ['h', `4. Importa tus ventas anteriores`],
        ['p', `En la página de Propiedades, importa tus Ventas como agente del comprador y Ventas como agente del vendedor; cada una tiene un ⓘ con los pasos de exportación. Las ventas del lado del vendedor se vinculan automáticamente a tus contactos, y la fecha de venta impulsa el recordatorio de “aniversario en su hogar”. Tus ventas también forman el historial que se muestra en tu perfil público.`],
        ['h', `5. Deja que los recordatorios se envíen`],
        ['p', `Los recordatorios —cumpleaños, aniversarios de compra y fiestas— se envían automáticamente según lo programado. Tú no pulsas enviar. Revisa Asignar recordatorios para ver lo que está configurado y agrega datos como cumpleaños cuando los tengas.`],
        ['h', `Preguntas frecuentes`],
        ['ul', [
          `Iniciar sesión: tu correo es tu usuario. ¿Olvidaste tu contraseña? Usa “¿Olvidaste tu contraseña?” en la pantalla de inicio de sesión.`,
          `Un recordatorio de cumpleaños no se envía: el contacto necesita una fecha de nacimiento registrada; agrégala y se activa automáticamente.`,
          `Faltan los botones de importar: tu MLS debe estar configurado (se elige al registrarte) para habilitar la importación.`,
          `Los clics en tu enlace de perfil no se registran: asegúrate de insertarlo como un enlace real en tu firma con “Insertar el enlace a mi perfil”.`,
        ]],
        ['note', `¿Necesitas ayuda? Escríbenos a support@iBetterAgent.com.`],
      ],
    },
  },

  terms: {
    en: {
      title: 'Terms of Use',
      blocks: [
        ['meta', `Last updated: July 2026`],
        ['lead', `Please read these Terms of Use (“Terms”) carefully before using iBetterAgent (the “Service”). By creating an account or using the Service, you agree to these Terms.`],
        ['h', `1. Your account`],
        ['p', `You are responsible for keeping your login credentials secure and for all activity under your account. Notify us immediately of any unauthorized use.`],
        ['h', `2. Fees & subscription`],
        ['p', `iBetterAgent may be offered on a free trial and on paid subscription plans. When a free trial ends, continued access requires an active paid subscription; if a trial or subscription lapses, access to the Service may be suspended or limited until it is renewed. Subscriptions and payment are arranged with iBetterAgent, and plans and prices may change with reasonable notice. Except where required by law, fees already paid are non-refundable.`],
        ['h', `3. Acceptable use`],
        ['p', `You agree to use the Service lawfully. When sending messages to your contacts you must: only contact people who have a relationship with you and expect to hear from you; comply with all applicable anti-spam and communications laws (such as CAN-SPAM and CASL); honor opt-out requests promptly; and not send unsolicited bulk, unlawful, harassing, or deceptive content.`],
        ['h', `4. Your data and content`],
        ['p', `You retain ownership of the client and property information you enter. You are responsible for having the right to store that information and to contact those individuals, and for complying with the rules of your MLS, brokerage, or data providers.`],
        ['h', `5. Third-party services`],
        ['p', `The Service may connect to third parties such as MLS data feeds and email-delivery providers. Your use of those services is subject to their own terms, and we are not responsible for them.`],
        ['h', `6. Service availability & disclaimers`],
        ['p', `The Service is provided “as is” and “as available,” without warranties of any kind. We do not guarantee that every reminder or email will be delivered on time or at all, and you should not rely on the Service as the sole record of your client commitments.`],
        ['h', `7. Limitation of liability`],
        ['p', `To the fullest extent permitted by law, iBetterAgent and its operators are not liable for any indirect, incidental, or consequential damages, or for lost business, missed reminders, or lost data arising from your use of the Service.`],
        ['h', `8. Termination`],
        ['p', `You may stop using the Service at any time. We may suspend or terminate access for violations of these Terms.`],
        ['h', `9. Changes`],
        ['p', `We may update these Terms from time to time. Continued use after changes take effect constitutes acceptance of the updated Terms.`],
        ['h', `10. Contact`],
        ['p', `Questions about these Terms? Email support@iBetterAgent.com.`],
      ],
    },
    zh: {
      title: '使用条款',
      blocks: [
        ['meta', `最后更新：2026年7月`],
        ['lead', `在使用 iBetterAgent（“本服务”）之前，请仔细阅读本《使用条款》（“条款”）。创建账户或使用本服务即表示您同意本条款。`],
        ['h', `1. 您的账户`],
        ['p', `您有责任妥善保管登录凭据，并对账户下的所有活动负责。如发现任何未经授权的使用，请立即通知我们。`],
        ['h', `2. 费用与订阅`],
        ['p', `iBetterAgent 可能以免费试用及付费订阅计划提供。免费试用结束后，继续使用需要有效的付费订阅；若试用或订阅到期，本服务的访问权限可能被暂停或受限，直至续订。订阅与付款由 iBetterAgent 安排，计划和价格可能在合理通知后变更。除法律另有要求外，已支付的费用不予退还。`],
        ['h', `3. 可接受的使用`],
        ['p', `您同意合法使用本服务。向联系人发送消息时，您必须：仅联系与您有关系且预期会收到您消息的人；遵守所有适用的反垃圾邮件和通讯法律（如 CAN-SPAM 和 CASL）；及时尊重退订请求；不得发送未经请求的群发、违法、骚扰或欺骗性内容。`],
        ['h', `4. 您的数据与内容`],
        ['p', `您保留对所录入的客户和房产信息的所有权。您有责任确保拥有存储该信息及联系相关个人的权利，并遵守您所属 MLS、经纪公司或数据提供方的规定。`],
        ['h', `5. 第三方服务`],
        ['p', `本服务可能连接到 MLS 数据源和邮件投递服务等第三方。您对这些服务的使用受其各自条款约束，我们对此不承担责任。`],
        ['h', `6. 服务可用性与免责声明`],
        ['p', `本服务按“现状”和“现有”提供，不作任何形式的保证。我们不保证每条提醒或邮件都会准时或成功送达，您不应将本服务作为客户事项的唯一记录。`],
        ['h', `7. 责任限制`],
        ['p', `在法律允许的最大范围内，对于因您使用本服务而产生的任何间接、附带或后果性损害，或业务损失、漏发提醒或数据丢失，iBetterAgent 及其运营方概不负责。`],
        ['h', `8. 终止`],
        ['p', `您可随时停止使用本服务。对于违反本条款的行为，我们可暂停或终止访问权限。`],
        ['h', `9. 变更`],
        ['p', `我们可能会不时更新本条款。变更生效后继续使用即表示接受更新后的条款。`],
        ['h', `10. 联系方式`],
        ['p', `对本条款有疑问？请发送邮件至 support@iBetterAgent.com。`],
      ],
    },
    es: {
      title: 'Términos de uso',
      blocks: [
        ['meta', `Última actualización: julio de 2026`],
        ['lead', `Lee atentamente estos Términos de uso (“Términos”) antes de usar iBetterAgent (el “Servicio”). Al crear una cuenta o usar el Servicio, aceptas estos Términos.`],
        ['h', `1. Tu cuenta`],
        ['p', `Eres responsable de mantener seguras tus credenciales de acceso y de toda la actividad de tu cuenta. Notifícanos de inmediato cualquier uso no autorizado.`],
        ['h', `2. Tarifas y suscripción`],
        ['p', `iBetterAgent puede ofrecerse con una prueba gratuita y con planes de suscripción de pago. Cuando termina una prueba gratuita, el acceso continuado requiere una suscripción de pago activa; si una prueba o suscripción caduca, el acceso al Servicio puede suspenderse o limitarse hasta que se renueve. Las suscripciones y el pago se gestionan con iBetterAgent, y los planes y precios pueden cambiar con un aviso razonable. Salvo que la ley exija lo contrario, las tarifas ya pagadas no son reembolsables.`],
        ['h', `3. Uso aceptable`],
        ['p', `Aceptas usar el Servicio de forma lícita. Al enviar mensajes a tus contactos debes: contactar solo a personas con las que tengas una relación y que esperen saber de ti; cumplir todas las leyes aplicables contra el spam y de comunicaciones (como CAN-SPAM y CASL); atender con prontitud las solicitudes de baja; y no enviar contenido masivo no solicitado, ilícito, acosador o engañoso.`],
        ['h', `4. Tus datos y contenido`],
        ['p', `Conservas la propiedad de la información de clientes y propiedades que introduces. Eres responsable de tener el derecho a almacenar esa información y a contactar a esas personas, y de cumplir las reglas de tu MLS, agencia o proveedores de datos.`],
        ['h', `5. Servicios de terceros`],
        ['p', `El Servicio puede conectarse con terceros, como fuentes de datos MLS y proveedores de envío de correo. Tu uso de esos servicios está sujeto a sus propios términos, y no somos responsables de ellos.`],
        ['h', `6. Disponibilidad y descargos`],
        ['p', `El Servicio se ofrece “tal cual” y “según disponibilidad”, sin garantías de ningún tipo. No garantizamos que cada recordatorio o correo se entregue a tiempo o en absoluto, y no debes basarte en el Servicio como único registro de tus compromisos con clientes.`],
        ['h', `7. Limitación de responsabilidad`],
        ['p', `En la máxima medida permitida por la ley, iBetterAgent y sus operadores no son responsables de daños indirectos, incidentales o consecuentes, ni de pérdida de negocio, recordatorios no enviados o pérdida de datos derivados de tu uso del Servicio.`],
        ['h', `8. Terminación`],
        ['p', `Puedes dejar de usar el Servicio en cualquier momento. Podemos suspender o cancelar el acceso por incumplimiento de estos Términos.`],
        ['h', `9. Cambios`],
        ['p', `Podemos actualizar estos Términos ocasionalmente. El uso continuado tras la entrada en vigor de los cambios constituye la aceptación de los Términos actualizados.`],
        ['h', `10. Contacto`],
        ['p', `¿Preguntas sobre estos Términos? Escribe a support@iBetterAgent.com.`],
      ],
    },
  },

  privacy: {
    en: {
      title: 'Privacy Notice',
      blocks: [
        ['meta', `Last updated: July 2026`],
        ['lead', `This Privacy Notice explains what information iBetterAgent collects, how it is used, and the choices you have.`],
        ['h', `Information we collect`],
        ['ul', [
          `Account information you provide — such as username, email, alias, phone, and MLS ID.`,
          `Client and property information you enter — such as contacts' names, emails, phone numbers, birthdays, and property addresses and sale details.`,
          `Usage information needed to operate the app, such as your reminder settings and send history.`,
          `Email engagement data — whether the messages you send are delivered, opened, or clicked — which powers the engagement insights shown on your Contacts.`,
        ]],
        ['h', `How we use it`],
        ['p', `We use this information solely to provide the Service: to store and organize your data, to send the reminder messages you configure to the recipients you choose, and to show you engagement insights based on how your contacts interact with those messages. We do not sell your data.`],
        ['h', `Public profile`],
        ['p', `If you publish a public agent profile, the details you choose to include — your photo, bio, contact information, sold listings, and approved client reviews — are publicly viewable by anyone with the link.`],
        ['h', `Sharing`],
        ['p', `Information is shared only as needed to run the Service — for example, with the email-delivery provider used to send your messages, or with MLS/data providers you choose to connect. These providers process data on your behalf under their own terms.`],
        ['h', `Your clients' data`],
        ['p', `As the agent, you are the controller of your clients' information. You are responsible for having a lawful basis to store and contact them, and for honoring their privacy and opt-out requests.`],
        ['h', `Storage, security & retention`],
        ['p', `We take reasonable measures to protect your information. Data is retained until you delete it or close your account, after which it is removed. You can access, correct, or delete your information at any time from within the app.`],
        ['h', `Your choices`],
        ['p', `You may update or delete contacts, properties, templates, and your account data directly in the app. To request deletion of your entire account, contact us.`],
        ['h', `Children`],
        ['p', `The Service is intended for real estate professionals and is not directed to anyone under 18.`],
        ['h', `Changes & contact`],
        ['p', `We may update this notice from time to time. Questions or requests? Email support@iBetterAgent.com.`],
      ],
    },
    zh: {
      title: '隐私声明',
      blocks: [
        ['meta', `最后更新：2026年7月`],
        ['lead', `本隐私声明说明 iBetterAgent 收集哪些信息、如何使用，以及您拥有的选择。`],
        ['h', `我们收集的信息`],
        ['ul', [
          `您提供的账户信息——如用户名、电子邮箱、别名、电话和 MLS ID。`,
          `您录入的客户和房产信息——如联系人的姓名、邮箱、电话、生日，以及房产地址和成交详情。`,
          `运行应用所需的使用信息，如您的提醒设置和发送历史。`,
          `邮件互动数据——您发送的消息是否已送达、打开或点击——用于在“联系人”中为您展示互动分析。`,
        ]],
        ['h', `我们如何使用`],
        ['p', `我们仅将这些信息用于提供本服务：存储和整理您的数据，将您配置的提醒消息发送给您选择的收件人，并根据联系人与这些消息的互动情况为您展示互动分析。我们不会出售您的数据。`],
        ['h', `公开主页`],
        ['p', `如果您发布公开的经纪人主页，您选择包含的信息——照片、简介、联系方式、售出房源和已批准的客户评价——任何持有链接的人均可公开查看。`],
        ['h', `信息共享`],
        ['p', `信息仅在运行本服务所需时共享——例如，与用于发送消息的邮件投递服务，或您选择连接的 MLS／数据提供方共享。这些提供方按其各自条款代表您处理数据。`],
        ['h', `您客户的数据`],
        ['p', `作为经纪人，您是客户信息的控制者。您有责任拥有存储和联系他们的合法依据，并尊重他们的隐私和退订请求。`],
        ['h', `存储、安全与保留`],
        ['p', `我们采取合理措施保护您的信息。数据将一直保留，直到您将其删除或关闭账户后被移除。您可随时在应用内访问、更正或删除您的信息。`],
        ['h', `您的选择`],
        ['p', `您可直接在应用内更新或删除联系人、房产、模板和账户数据。如需删除整个账户，请联系我们。`],
        ['h', `未成年人`],
        ['p', `本服务面向房地产专业人士，不面向 18 岁以下人士。`],
        ['h', `变更与联系`],
        ['p', `我们可能会不时更新本声明。有疑问或请求？请发送邮件至 support@iBetterAgent.com。`],
      ],
    },
    es: {
      title: 'Aviso de privacidad',
      blocks: [
        ['meta', `Última actualización: julio de 2026`],
        ['lead', `Este Aviso de privacidad explica qué información recopila iBetterAgent, cómo se usa y qué opciones tienes.`],
        ['h', `Información que recopilamos`],
        ['ul', [
          `Información de la cuenta que proporcionas, como usuario, correo, alias, teléfono e ID de MLS.`,
          `Información de clientes y propiedades que introduces, como nombres, correos, teléfonos y cumpleaños de los contactos, y direcciones y detalles de venta de las propiedades.`,
          `Información de uso necesaria para operar la app, como tus ajustes de recordatorios y el historial de envíos.`,
          `Datos de interacción con el correo —si los mensajes que envías se entregan, se abren o reciben clics—, que impulsan la información de interacción que ves en tus Contactos.`,
        ]],
        ['h', `Cómo la usamos`],
        ['p', `Usamos esta información únicamente para prestar el Servicio: almacenar y organizar tus datos, enviar los mensajes de recordatorio que configures a los destinatarios que elijas, y mostrarte información de interacción según cómo tus contactos interactúan con esos mensajes. No vendemos tus datos.`],
        ['h', `Perfil público`],
        ['p', `Si publicas un perfil público de agente, los datos que decidas incluir —tu foto, biografía, información de contacto, propiedades vendidas y reseñas de clientes aprobadas— son visibles públicamente para cualquiera que tenga el enlace.`],
        ['h', `Compartir`],
        ['p', `La información se comparte solo cuando es necesario para operar el Servicio, por ejemplo, con el proveedor de envío de correo usado para enviar tus mensajes, o con los proveedores de MLS/datos que elijas conectar. Estos proveedores procesan los datos en tu nombre según sus propios términos.`],
        ['h', `Los datos de tus clientes`],
        ['p', `Como agente, eres el responsable del tratamiento de la información de tus clientes. Es tu responsabilidad tener una base legal para almacenarla y contactarlos, y respetar su privacidad y sus solicitudes de baja.`],
        ['h', `Almacenamiento, seguridad y conservación`],
        ['p', `Tomamos medidas razonables para proteger tu información. Los datos se conservan hasta que los elimines o cierres tu cuenta, tras lo cual se eliminan. Puedes acceder, corregir o eliminar tu información en cualquier momento desde la app.`],
        ['h', `Tus opciones`],
        ['p', `Puedes actualizar o eliminar contactos, propiedades, plantillas y los datos de tu cuenta directamente en la app. Para solicitar la eliminación de toda tu cuenta, contáctanos.`],
        ['h', `Menores`],
        ['p', `El Servicio está destinado a profesionales inmobiliarios y no se dirige a menores de 18 años.`],
        ['h', `Cambios y contacto`],
        ['p', `Podemos actualizar este aviso ocasionalmente. ¿Preguntas o solicitudes? Escribe a support@iBetterAgent.com.`],
      ],
    },
  },
  contact: {
    en: {
      title: 'Contact Us',
      blocks: [
        ['lead', `We'd love to hear from you — whether you have a question, feedback, or need help getting set up.`],
        ['h', `Email`],
        ['p', `Support & general inquiries: support@iBetterAgent.com`],
        ['note', `We typically reply within one business day.`],
      ],
    },
    zh: {
      title: '联系我们',
      blocks: [
        ['lead', `我们很乐意听到您的声音——无论是疑问、反馈，还是需要设置方面的帮助。`],
        ['h', `电子邮箱`],
        ['p', `支持与一般咨询：support@iBetterAgent.com`],
        ['note', `我们通常会在一个工作日内回复。`],
      ],
    },
    es: {
      title: 'Contáctanos',
      blocks: [
        ['lead', `Nos encantaría saber de ti, ya sea que tengas una pregunta, comentarios o necesites ayuda para empezar.`],
        ['h', `Correo electrónico`],
        ['p', `Soporte y consultas generales: support@iBetterAgent.com`],
        ['note', `Normalmente respondemos en un día hábil.`],
      ],
    },
  },
};

// Turn any email address inside a text string into a clickable mailto: link
// (e.g. support@iBetterAgent.com). Works across all languages/blocks in one place.
const EMAIL_ONE = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
function linkifyEmails(text) {
  if (typeof text !== 'string') return text;
  const parts = text.split(/([A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,})/g);
  if (parts.length === 1) return text;
  return parts.map((part, i) =>
    EMAIL_ONE.test(part) ? <a key={i} href={`mailto:${part}`}>{part}</a> : part
  );
}

function renderBlocks(blocks) {
  return blocks.map((b, i) => {
    const [type, val] = b;
    switch (type) {
      case 'h':    return <h3 key={i}>{linkifyEmails(val)}</h3>;
      case 'lead': return <p key={i} className="info-lead">{linkifyEmails(val)}</p>;
      case 'meta': return <p key={i} className="info-meta">{linkifyEmails(val)}</p>;
      case 'note': return <p key={i} className="info-note">{linkifyEmails(val)}</p>;
      case 'ul':   return <ul key={i}>{val.map((li, j) => <li key={j}>{linkifyEmails(li)}</li>)}</ul>;
      case 'link': return <p key={i} className="info-note"><a className="info-doc-link" href={val[1]}>{val[0]}</a></p>;
      case 'cta':  return <p key={i} className="info-cta-row"><a className="info-cta" href={val[1]}>{val[0]}</a></p>;
      case 'offer': return (
        <div key={i} className="info-offer">
          <span className="info-offer-badge">{val[0]}</span>
          <div className="info-offer-main">{val[1]}</div>
          <div className="info-offer-sub">{val[2]}</div>
        </div>
      );
      default:     return <p key={i}>{linkifyEmails(val)}</p>;
    }
  });
}

function InfoPage({ page, onBack, onNavigate, onGetStarted }) {
  const { t, lang } = useI18n();
  const year = new Date().getFullYear();
  const pageDef = PAGES[page] || PAGES.about;
  const data = pageDef[lang] || pageDef.en;

  return (
    <div className="info-page">
      <header className="info-nav">
        <button className="info-brand" onClick={onBack}><span className="info-logo"><Icon name="brand" size={22} /></span> iBetterAgent</button>
        <div className="info-nav-actions">
          {onGetStarted && <button className="info-signin" onClick={onGetStarted}>{t('landing.signin')}</button>}
        </div>
      </header>

      <main className="info-content">
        <button className="info-back" onClick={onBack}>{t('info.back')}</button>
        <article className="info-article">
          <h1>{data.title}</h1>
          {renderBlocks(data.blocks)}
        </article>
      </main>

      <footer className="info-footer">
        <div className="info-footer-links">
          {INFO_LINKS.map(l => (
            <Fragment key={l.key}>
              <button
                className={`info-footer-link ${l.key === page ? 'active' : ''}`}
                onClick={() => onNavigate(l.key)}>{t(`info.${l.key}`)}</button>
              {/* Static marketing flyer (client/public) — a real page, not an InfoPage view. */}
              {l.key === 'about' && <a className="info-footer-link" href="/why-ibetteragent.html">{t('info.why')}</a>}
            </Fragment>
          ))}
        </div>
        <div className="info-footer-copy">© {year} iBetterAgent</div>
      </footer>
    </div>
  );
}

export default InfoPage;
